import { CSSProperties } from 'react';
import { identity, last } from 'ramda';
import { noop } from './noop';

export type GetVideoFramesOptions = {
  src: string;
  width?: number;
  height?: number;
  fps?: number;
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  onProgress?: (progress: number) => void;
  processFrame?: (data: ImageData) => ImageData;
};

export type VideoFrames = {
  frames: ImageData[];
  width: number;
  height: number;
  getFrameByTime: (time: number) => ImageData;
};

const createGetFrameByTime = (
  frames: ImageData[],
  fps: number,
  duration: number
): VideoFrames['getFrameByTime'] => {
  return time => {
    if (time <= 0) {
      return frames[0];
    }
    if (time >= duration * 1000) {
      return last(frames);
    }

    return frames.find((_, i) => i * (1000 / fps) >= time);
  };
};

export const getVideoFrames = async ({
  width: customWidth,
  height: customHeight,
  src,
  fps = 30,
  objectFit = 'cover',
  objectPosition = '50% 50%',
  onProgress = noop,
  processFrame = identity
}: GetVideoFramesOptions): Promise<VideoFrames> => {
  const frames: ImageData[] = [];

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const video = document.createElement('video');
  video.style.objectFit = objectFit;
  video.style.objectPosition = objectPosition as any;
  video.muted = true;
  video.crossOrigin = 'Anonymous';

  let width: number, height: number;

  const duration = await new Promise<number>(resolve => {
    video.onloadeddata = () => {
      width = customWidth ?? video.videoWidth;
      height = customHeight ?? video.videoHeight;

      canvas.width = width;
      canvas.height = height;
      video.width = width;
      video.height = height;

      resolve(video.duration);
    };

    video.src = src;
  });

  return new Promise(resolve => {
    const onTimeUpdate = () => {
      const { currentTime } = video;
      if (currentTime < duration) {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(video, 0, 0, width, height);

        const frameData = ctx.getImageData(0, 0, width, height);
        const processedFrameData = processFrame(frameData);
        frames.push(processedFrameData);

        onProgress((currentTime / duration) * 100);
        video.currentTime = Math.min(duration, currentTime + 1 / fps);
      } else {
        resolve({
          frames,
          height,
          width,
          getFrameByTime: createGetFrameByTime(frames, fps, duration)
        });
      }
    };

    video.ontimeupdate = onTimeUpdate;
    onTimeUpdate();
  });
};
